<template>
  <div class="output-query">
    <el-form
      :inline="true"
      class="query-form"
      size="small"
      @submit.native.prevent
    >
      <el-form-item label="文件名">
        <el-input
          v-model.trim="filters.fileName"
          placeholder="输入文件名或路径片段"
          maxlength="255"
          clearable
          :disabled="loading"
          @keyup.enter.native="handleSearch"
        />
      </el-form-item>
      <el-form-item label="导出类型">
        <el-select
          v-model="filters.type"
          clearable
          placeholder="全部类型"
          :disabled="loading"
        >
          <el-option
            v-for="item in types"
            :key="item.value"
            :label="item.label"
            :value="item.value"
          />
        </el-select>
      </el-form-item>
      <el-form-item label="文件后缀">
        <el-input
          v-model.trim="filters.suffix"
          placeholder="如 rar、zip"
          maxlength="20"
          clearable
          :disabled="loading"
        />
      </el-form-item>
      <el-form-item label="导出日期">
        <el-date-picker
          v-model="filters.dates"
          type="daterange"
          value-format="yyyy-MM-dd"
          start-placeholder="开始日期"
          end-placeholder="结束日期"
          range-separator="至"
          :disabled="loading"
        />
      </el-form-item>
      <el-form-item label="记录状态">
        <el-select v-model="filters.deleted" :disabled="loading">
          <el-option label="有效记录" :value="0" />
          <el-option label="已删除记录" :value="1" />
          <el-option label="全部记录" value="all" />
        </el-select>
      </el-form-item>
      <el-form-item>
        <el-button
          type="primary"
          icon="el-icon-search"
          :loading="loading"
          @click="handleSearch"
          >查询</el-button
        >
        <el-button
          icon="el-icon-refresh"
          :disabled="loading"
          @click="resetSearch"
          >重置</el-button
        >
      </el-form-item>
    </el-form>

    <div class="table-toolbar">
      <span
        >导出文件记录<span class="record-count">共 {{ total }} 条</span></span
      >
      <el-checkbox v-model="showPasswords">显示解压密码</el-checkbox>
    </div>
    <el-alert
      v-if="queryFailed"
      title="查询失败，请重新查询。"
      type="error"
      :closable="false"
      show-icon
    />
    <el-table
      v-loading="loading"
      :data="records"
      stripe
      border
      height="100%"
      class="records-table"
    >
      <template slot="empty">{{
        loading ? "数据加载中" : queryFailed ? "查询失败" : "没有匹配的记录"
      }}</template>
      <el-table-column prop="id" label="记录 ID" width="190" />
      <el-table-column
        prop="fileName"
        label="文件名 / 路径"
        min-width="300"
        show-overflow-tooltip
      />
      <el-table-column label="导出类型" width="120">
        <template slot-scope="scope">{{ typeLabel(scope.row.type) }}</template>
      </el-table-column>
      <el-table-column prop="suffix" label="后缀" width="80" />
      <el-table-column prop="outTime" label="导出时间" width="170" />
      <el-table-column label="解压密码" width="160">
        <template slot-scope="scope"
          ><span class="password-text">{{
            passwordLabel(scope.row.password)
          }}</span></template
        >
      </el-table-column>
      <el-table-column
        prop="createBy"
        label="创建人"
        width="110"
        show-overflow-tooltip
      />
      <el-table-column label="状态" width="90" align="center">
        <template slot-scope="scope">
          <el-tag
            :type="scope.row.deleted === 1 ? 'info' : 'success'"
            size="mini"
            >{{ scope.row.deleted === 1 ? "已删除" : "有效" }}</el-tag
          >
        </template>
      </el-table-column>
      <el-table-column label="操作" width="190" fixed="right">
        <template slot-scope="scope">
          <el-button
            type="text"
            size="small"
            @click="selectedRecord = scope.row"
            >查看</el-button
          >
          <el-button
            type="text"
            size="small"
            @click="copyValue(scope.row.fileName, '文件名')"
            >复制文件名</el-button
          >
          <el-button
            type="text"
            size="small"
            :disabled="!scope.row.password"
            @click="copyValue(scope.row.password, '密码')"
            >复制密码</el-button
          >
        </template>
      </el-table-column>
    </el-table>
    <div class="pagination">
      <el-pagination
        :current-page="page"
        :page-size="size"
        :page-sizes="[20, 50, 100]"
        :total="total"
        layout="total, sizes, prev, pager, next, jumper"
        :disabled="loading"
        @current-change="changePage"
        @size-change="changeSize"
      />
    </div>

    <el-dialog
      title="导出记录详情"
      :visible="!!selectedRecord"
      width="760px"
      @close="selectedRecord = null"
    >
      <el-descriptions v-if="selectedRecord" :column="2" border size="small">
        <el-descriptions-item label="记录 ID">{{
          selectedRecord.id
        }}</el-descriptions-item>
        <el-descriptions-item label="记录状态">{{
          selectedRecord.deleted === 1 ? "已删除" : "有效"
        }}</el-descriptions-item>
        <el-descriptions-item label="文件名 / 路径" :span="2"
          ><span class="file-path">{{
            selectedRecord.fileName
          }}</span></el-descriptions-item
        >
        <el-descriptions-item label="导出类型">{{
          typeLabel(selectedRecord.type)
        }}</el-descriptions-item>
        <el-descriptions-item label="文件后缀">{{
          selectedRecord.suffix || "—"
        }}</el-descriptions-item>
        <el-descriptions-item label="导出时间">{{
          selectedRecord.outTime || "—"
        }}</el-descriptions-item>
        <el-descriptions-item label="解压密码">
          <span class="password-text">{{
            passwordLabel(selectedRecord.password)
          }}</span>
          <el-button
            v-if="selectedRecord.password"
            type="text"
            size="mini"
            @click="copyValue(selectedRecord.password, '密码')"
            >复制</el-button
          >
        </el-descriptions-item>
        <el-descriptions-item label="创建人">{{
          selectedRecord.createBy || "—"
        }}</el-descriptions-item>
        <el-descriptions-item label="创建时间">{{
          selectedRecord.createTime || "—"
        }}</el-descriptions-item>
        <el-descriptions-item label="更新人">{{
          selectedRecord.updateBy || "—"
        }}</el-descriptions-item>
        <el-descriptions-item label="更新时间">{{
          selectedRecord.updateTime || "—"
        }}</el-descriptions-item>
        <el-descriptions-item label="删除人">{{
          selectedRecord.deleteBy || "—"
        }}</el-descriptions-item>
        <el-descriptions-item label="删除时间">{{
          selectedRecord.deleteTime || "—"
        }}</el-descriptions-item>
      </el-descriptions>
      <div slot="footer">
        <el-checkbox v-model="showPasswords" class="password-toggle"
          >显示解压密码</el-checkbox
        >
        <el-button size="small" @click="selectedRecord = null">关闭</el-button>
      </div>
    </el-dialog>
  </div>
</template>

<script>
import { queryOutPutRecords } from "@/api/outPutAdmin";
import { copyText } from "@/utils/copyText";

function defaultFilters() {
  return { fileName: "", type: "", suffix: "", dates: [], deleted: 0 };
}

export default {
  name: "OutPutQuery",
  data() {
    return {
      filters: defaultFilters(),
      appliedFilters: {},
      types: [
        { value: 1, label: "校拍打包" },
        { value: 2, label: "散拍打包" },
        { value: 3, label: "未采集学生" },
        { value: 4, label: "未预约学生" },
      ],
      records: [],
      total: 0,
      page: 1,
      size: 20,
      loading: false,
      queryFailed: false,
      showPasswords: false,
      selectedRecord: null,
      requestSequence: 0,
    };
  },
  created() {
    this.handleSearch();
  },
  beforeDestroy() {
    // 离开页面后忽略尚未完成的查询结果，不继续保留文件密码。
    this.requestSequence += 1;
    this.records = [];
    this.selectedRecord = null;
  },
  methods: {
    handleSearch() {
      const dates = this.filters.dates || [];
      this.appliedFilters = {
        fileName: this.filters.fileName,
        type: this.filters.type || null,
        suffix: this.filters.suffix,
        deleted: this.filters.deleted === "all" ? null : this.filters.deleted,
        startDate: dates[0] || null,
        endDate: dates[1] || null,
      };
      this.page = 1;
      return this.loadRecords();
    },
    resetSearch() {
      this.filters = defaultFilters();
      return this.handleSearch();
    },
    async loadRecords() {
      const sequence = ++this.requestSequence;
      this.loading = true;
      this.queryFailed = false;
      this.showPasswords = false;
      this.selectedRecord = null;
      try {
        // 翻页沿用上次已提交的筛选条件，避免输入框尚未查询的内容影响结果。
        const response = await queryOutPutRecords({
          ...this.appliedFilters,
          page: this.page,
          size: this.size,
        });
        if (sequence !== this.requestSequence) return;
        this.records = response.data.content || [];
        this.total = response.data.totalElements || 0;
      } catch (error) {
        if (sequence !== this.requestSequence) return;
        this.records = [];
        this.total = 0;
        this.queryFailed = true;
      } finally {
        if (sequence === this.requestSequence) this.loading = false;
      }
    },
    changePage(page) {
      this.page = page;
      return this.loadRecords();
    },
    changeSize(size) {
      this.size = size;
      this.page = 1;
      return this.loadRecords();
    },
    typeLabel(type) {
      const item = this.types.find((value) => value.value === type);
      return item ? item.label : type == null ? "未设置" : `类型 ${type}`;
    },
    passwordLabel(password) {
      return password ? (this.showPasswords ? password : "••••••") : "未设置";
    },
    async copyValue(value, label) {
      if (!value) return;
      try {
        await copyText(value);
        this.$message.success(`${label}已复制`);
      } catch (error) {
        this.$message.warning("复制失败，请在详情中手动复制");
      }
    },
  },
};
</script>

<style scoped>
.output-query {
  display: flex;
  flex-direction: column;
  height: calc(100vh - 140px);
  min-height: 380px;
}
.query-form {
  padding: 16px 16px 0;
  background: #f7f9fc;
  border: 1px solid #e4e7ed;
  border-radius: 4px;
}
.query-form .el-input {
  width: 230px;
}
.query-form .el-select {
  width: 145px;
}
.query-form .el-range-editor {
  width: 290px;
}
.table-toolbar {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 18px 0 12px;
  color: #303133;
}
.record-count {
  margin-left: 12px;
  font-size: 13px;
  color: #909399;
}
.records-table {
  flex: 1;
  min-height: 0;
}
.pagination {
  padding-top: 16px;
  text-align: right;
}
.password-text {
  font-family: monospace;
  overflow-wrap: anywhere;
}
.file-path {
  word-break: break-all;
}
.password-toggle {
  margin-right: 20px;
}
</style>
